import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bot, Copy, Check, Unplug, Loader2, ShieldCheck, ExternalLink } from 'lucide-react';
import { api } from '../../lib/api';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';

const ROLE_LABEL = { admin: 'Admin', analyst: 'Analyst', ac_member: 'AC-Mitglied' };
const fmt = (iso) => iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

// Team & Roles (admin): connector URL + setup steps, per-user Claude access, active connections.
export function ClaudeConnectorCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(false);
  const load = () => api.mcpAdminOverview().then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (u, enabled) => {
    setBusy(u.id);
    try { const r = await api.mcpSetAccess(u.id, enabled); toast.success(r.data.message); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    setBusy(null);
  };
  const revoke = async (c) => {
    if (!window.confirm(`Verbindung von ${c.user_name} trennen? Claude muss sich danach neu anmelden.`)) return;
    setBusy(c.family_id);
    try { const r = await api.mcpRevokeConnection(c.family_id); toast.success(r.data.message); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    setBusy(null);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.connector_url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { toast.error('Kopieren nicht möglich'); }
  };

  if (!data) return <div className="bg-card border border-border rounded-sm p-4 text-xs text-muted-foreground" data-testid="claude-connector-card">Lade Claude-Connector…</div>;
  const connections = data.users.flatMap(u => u.connections);

  return (
    <div className="bg-card border border-border rounded-sm p-4 sm:p-5 space-y-5" data-testid="claude-connector-card">
      <div>
        <h3 className="font-heading text-lg text-foreground flex items-center gap-2 mb-1">
          <Bot style={{ width: 18, height: 18 }} className="text-orange-400" /> Claude-Connector
        </h3>
        <p className="text-xs text-muted-foreground">
          OLTA in Claude nutzen („Wie ist der Stand bei STS?“, „Firmen im Tankreinigungs-Bereich“, „Wie viele Briefe haben wir verschickt?“).
          Claude fragt live und nur lesend in OLTA nach — jede Person meldet sich einmal mit ihrem OLTA-Login an und sieht genau das, was ihre Rolle erlaubt.
        </p>
      </div>

      <div className="border border-orange-500/30 bg-orange-500/5 rounded-sm p-3 space-y-2" data-testid="claude-connector-setup">
        <p className="text-xs text-foreground font-medium">Einrichtung in Claude (Team/Enterprise: ein Owner, einmalig)</p>
        <ol className="text-[11px] text-muted-foreground space-y-1 list-decimal list-inside">
          <li>claude.ai → Einstellungen → <span className="text-foreground">Connectors</span> → „Custom connector hinzufügen“</li>
          <li>Name „OLTA Nachfolge“, als URL diese Adresse einfügen (ohne Anmeldedaten — Login kommt im nächsten Schritt):</li>
        </ol>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-[11px] font-mono bg-secondary border border-border rounded-sm px-2 py-1.5 text-foreground break-all" data-testid="claude-connector-url">{data.connector_url}</code>
          <Button size="sm" variant="outline" onClick={copy} className="h-8 rounded-sm text-[11px]" data-testid="claude-connector-copy-btn">
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
          </Button>
        </div>
        <ol start={3} className="text-[11px] text-muted-foreground space-y-1 list-decimal list-inside">
          <li>„Verbinden“ → OLTA-Login öffnet sich → <span className="text-foreground">Zugriff erlauben</span>. Jedes Teammitglied macht diesen Schritt einmal selbst (Connector → Verbinden).</li>
          <li>Danach in jedem Chat den OLTA-Connector aktivieren und einfach fragen.</li>
        </ol>
        <a href="https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp" target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-sky-300 hover:underline" data-testid="claude-connector-help-link">
          Anleitung von Anthropic <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      <div data-testid="claude-access-list">
        <p className="text-xs text-foreground font-medium mb-2 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Wer darf OLTA in Claude nutzen?</p>
        <div className="divide-y divide-border/60 border border-border rounded-sm">
          {data.users.map(u => (
            <div key={u.id} className="flex items-center gap-3 px-3 py-2" data-testid={`claude-access-row-${u.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-foreground truncate">{u.name} <span className="text-[10px] text-muted-foreground">· {ROLE_LABEL[u.role] || u.role}</span></p>
                <p className="text-[10px] text-muted-foreground truncate">{u.email}{u.connections.length ? ` · ${u.connections.length} aktive Verbindung${u.connections.length !== 1 ? 'en' : ''}` : ''}</p>
              </div>
              {u.fixed
                ? <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-blue-500/40 bg-blue-500/10 text-blue-300" data-testid={`claude-access-fixed-${u.id}`}>Admin · immer</span>
                : <Switch checked={u.allowed} disabled={busy === u.id} onCheckedChange={(v) => toggle(u, v)} data-testid={`claude-access-switch-${u.id}`} />}
            </div>
          ))}
        </div>
      </div>

      <div data-testid="claude-connections">
        <p className="text-xs text-foreground font-medium mb-2">Aktive Verbindungen <span className="text-muted-foreground font-normal">· {connections.length} · {data.total_calls} Anfragen gesamt</span></p>
        {connections.length === 0
          ? <p className="text-[11px] text-muted-foreground" data-testid="claude-connections-empty">Noch niemand verbunden.</p>
          : <div className="divide-y divide-border/60 border border-border rounded-sm">
            {connections.map(c => (
              <div key={c.family_id} className="flex items-center gap-3 px-3 py-2" data-testid={`claude-connection-${c.family_id}`}>
                <div className="min-w-0 flex-1 text-[11px]">
                  <p className="text-foreground">{c.user_name} <span className="text-muted-foreground">über {c.client_name}</span></p>
                  <p className="text-muted-foreground">verbunden {fmt(c.created_at)} · zuletzt {fmt(c.last_used_at)} · {c.calls} Anfragen</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => revoke(c)} disabled={busy === c.family_id} className="h-7 rounded-sm text-[10px]" data-testid={`claude-connection-revoke-${c.family_id}`}>
                  {busy === c.family_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Unplug className="w-3 h-3 mr-1" /> Trennen</>}
                </Button>
              </div>
            ))}
          </div>}
      </div>
    </div>
  );
}
